(() => {
  if (window.__wiAbodeProductGalleryAssetLoaded) {
    document
      .querySelectorAll('[data-wi-product-gallery]')
      .forEach((root) => window.WIAbodeProductGalleryInitialize?.(root));
    return;
  }

  window.__wiAbodeProductGalleryAssetLoaded = true;

  const initialize = (root) => {
    if (!root || root.dataset.wiReady === 'true') return;

    root.dataset.wiReady = 'true';

    const configElement = root.querySelector('[data-wi-product-config]');
    let config = {};

    try {
      config = JSON.parse(configElement?.textContent || '{}');
    } catch (error) {
      console.error(
        '[WI Abode Product Gallery] Invalid configuration JSON.',
        error
      );
    }

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion:reduce)'
    ).matches;

    const moneyFormat = config.moneyFormat || '${{amount}}';
    const locale =
      config.locale || document.documentElement.lang || 'en-IN';

    const currency =
      config.currency ||
      window.Shopify?.currency?.active ||
      'INR';

    const formatMoney = (cents) =>
      window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(cents, moneyFormat)
        : new Intl.NumberFormat(locale, {
            style: 'currency',
            currency
          }).format(Number(cents || 0) / 100);

    const stickySlot = root.querySelector(
      '[data-wi-sticky-slot]'
    );

    const sticky = root.querySelector('[data-wi-sticky]');

    const stickyMode =
      root.dataset.wiStickyMode || 'automatic';

    const stickyGap = Number(
      root.dataset.wiStickyGap || 0
    );

    const stickyZ = Number(
      root.dataset.wiStickyZ || 1200
    );

    const nav = sticky?.querySelector('.wi-pg__nav');

    const indicator = sticky?.querySelector(
      '[data-wi-indicator]'
    );

    const navButtons = Array.from(
      sticky?.querySelectorAll('[data-wi-target-part]') || []
    );

    const productForm = root.querySelector(
      'form[action*="/cart/add"]'
    );

    const primarySubmit = productForm?.querySelector(
      '[name="add"]'
    );

    const primarySubmitLabel = primarySubmit?.querySelector(
      'span:first-child'
    );

    const stickySubmitButtons = Array.from(
      root.querySelectorAll('[data-wi-submit-form]')
    );

    const productInfo = root.querySelector(
      '[data-wi-product-info]'
    );

    const shopRoot = window.Shopify?.routes?.root || '/';
    const cartAddUrl = `${shopRoot}cart/add.js`;
    const cartUrl = `${shopRoot}cart.js`;

    let stickyCartBusy = false;
    let stickyCartResetTimer = null;

    const headerSelector = [
      '[data-wi-header]',
      '#shopify-section-header',
      '.shopify-section-group-header-group',
      'sticky-header',
      'header.header-wrapper',
      '.header-wrapper',
      'header.site-header',
      'header[role="banner"]',
      '[data-sticky-header]'
    ].join(',');

    let headerCandidates = [];
    let productPartMap = new Map();
    let targets = [];
    let activePart = 'images';
    let navigationFrame = null;
    let indicatorFrame = null;
    let pendingIndicatorButton = null;
    let navigationLockPart = null;
    let navigationLockTimer = null;
    let lastFixed = null;
    let lastStickyTop = null;
    let lastStickyZ = null;

    const refreshHeaderCandidates = () => {
      headerCandidates = Array.from(
        document.querySelectorAll(headerSelector)
      )
        .filter(
          (element) =>
            !root.contains(element) && element !== sticky
        )
        .map((element) => {
          const z = parseInt(
            getComputedStyle(element).zIndex,
            10
          );

          return {
            element,
            z: Number.isFinite(z) ? z : 0
          };
        });
    };

    const refreshTargets = () => {
      productPartMap = new Map(
        Array.from(
          document.querySelectorAll('[data-wi-product-part]')
        ).map((element) => [
          element.dataset.wiProductPart,
          element
        ])
      );

      targets = [];

      navButtons.forEach((button) => {
        const part = button.dataset.wiTargetPart;
        const target = productPartMap.get(part);

        button.hidden = !target;

        if (target) {
          targets.push({
            part,
            element: target
          });
        }
      });

      if (
        !targets.some(
          (target) => target.part === activePart
        ) &&
        targets.length
      ) {
        activePart = targets[0].part;
      }
    };

    const refreshStructure = () => {
      refreshHeaderCandidates();
      refreshTargets();
    };

    const getHeaderMetrics = () => {
      const manual = Math.max(
        0,
        parseFloat(
          getComputedStyle(root).getPropertyValue(
            '--wi-pg-top'
          )
        ) || 0
      );

      if (stickyMode !== 'automatic') {
        return {
          top: manual,
          z: stickyZ
        };
      }

      let bottom = manual;
      let headerZ = 0;

      headerCandidates.forEach(({ element, z }) => {
        if (!element.isConnected) return;

        const rect = element.getBoundingClientRect();

        if (
          rect.top > 40 ||
          rect.bottom <= 0 ||
          rect.height < 2 ||
          rect.height > 400
        ) {
          return;
        }

        bottom = Math.max(bottom, rect.bottom);
        headerZ = Math.max(headerZ, z);
      });

      return {
        top: Math.max(0, bottom + stickyGap),
        z:
          headerZ > 1
            ? Math.max(
                20,
                Math.min(stickyZ, headerZ - 1)
              )
            : stickyZ
      };
    };

    const positionIndicator = (button) => {
      if (!nav || !indicator || !button) return;

      const navRect = nav.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      const inset = Math.min(
        16,
        Math.max(9, buttonRect.width * 0.17)
      );

      const nextX = (
        buttonRect.left -
        navRect.left +
        nav.scrollLeft +
        inset
      ).toFixed(2);

      const nextWidth = Math.max(
        20,
        buttonRect.width - inset * 2
      ).toFixed(2);

      if (indicator.dataset.x !== nextX) {
        indicator.style.transform =
          `translate3d(${nextX}px,0,0)`;

        indicator.dataset.x = nextX;
      }

      if (indicator.dataset.width !== nextWidth) {
        indicator.style.width = `${nextWidth}px`;
        indicator.dataset.width = nextWidth;
      }

      indicator.classList.add('is-ready');
    };

    const requestIndicator = (button) => {
      pendingIndicatorButton = button;

      if (indicatorFrame !== null) return;

      indicatorFrame = requestAnimationFrame(() => {
        indicatorFrame = null;

        positionIndicator(pendingIndicatorButton);

        pendingIndicatorButton = null;
      });
    };

    const keepActiveTabVisible = (button) => {
      if (
        !nav ||
        !button ||
        nav.scrollWidth <= nav.clientWidth + 1
      ) {
        return;
      }

      const buttonLeft = button.offsetLeft;
      const buttonRight =
        buttonLeft + button.offsetWidth;

      const visibleLeft = nav.scrollLeft + 14;
      const visibleRight =
        nav.scrollLeft + nav.clientWidth - 14;

      if (
        buttonLeft >= visibleLeft &&
        buttonRight <= visibleRight
      ) {
        return;
      }

      const nextLeft =
        buttonLeft -
        (nav.clientWidth - button.offsetWidth) / 2;

      nav.scrollTo({
        left: Math.max(0, nextLeft),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    };

    const setActive = (part, force = false) => {
      const button = navButtons.find(
        (item) =>
          item.dataset.wiTargetPart === part &&
          !item.hidden
      );

      if (!button) return false;

      const didChange =
        force ||
        activePart !== part ||
        !button.classList.contains('is-active');

      activePart = part;

      if (didChange) {
        navButtons.forEach((item) => {
          const active = item === button;

          item.classList.toggle(
            'is-active',
            active
          );

          if (active) {
            item.setAttribute(
              'aria-current',
              'location'
            );
          } else {
            item.removeAttribute('aria-current');
          }
        });

        keepActiveTabVisible(button);
      }

      requestIndicator(button);

      return true;
    };

    const applyStickyState = (
      shouldFix,
      metrics
    ) => {
      if (!sticky) return;

      if (lastFixed !== shouldFix) {
        sticky.classList.toggle(
          'is-fixed',
          shouldFix
        );

        lastFixed = shouldFix;
      }

      if (shouldFix) {
        if (lastStickyTop !== metrics.top) {
          sticky.style.setProperty(
            'top',
            `${metrics.top}px`,
            'important'
          );

          lastStickyTop = metrics.top;
        }

        if (lastStickyZ !== metrics.z) {
          sticky.style.setProperty(
            'z-index',
            String(metrics.z),
            'important'
          );

          lastStickyZ = metrics.z;
        }
      } else if (
        lastStickyTop !== null ||
        lastStickyZ !== null
      ) {
        sticky.style.removeProperty('top');
        sticky.style.removeProperty('z-index');

        lastStickyTop = null;
        lastStickyZ = null;
      }
    };

    const releaseNavigationLock = (
      refresh = true
    ) => {
      navigationLockPart = null;

      if (navigationLockTimer !== null) {
        clearTimeout(navigationLockTimer);
      }

      navigationLockTimer = null;

      if (refresh) {
        requestNavigationUpdate();
      }
    };

    const lockNavigationTo = (part) => {
      navigationLockPart = part;

      if (navigationLockTimer !== null) {
        clearTimeout(navigationLockTimer);
      }

      navigationLockTimer = setTimeout(
        () => releaseNavigationLock(),
        1600
      );
    };

    const updateNavigation = () => {
      navigationFrame = null;

      if (!stickySlot || !sticky) return;

      const metrics = getHeaderMetrics();
      const stickyHeight = sticky.offsetHeight;

      const marker =
        metrics.top + stickyHeight + 24;

      const slotTop =
        stickySlot.getBoundingClientRect().top;

      const rootBottom =
        root.getBoundingClientRect().bottom;

      const targetRects = targets.map(
        (target) => ({
          ...target,
          rect: target.element.getBoundingClientRect()
        })
      );

      const groupBottom = targetRects.reduce(
        (bottom, target) =>
          Math.max(bottom, target.rect.bottom),
        rootBottom
      );

      const shouldFix =
        slotTop <= metrics.top &&
        groupBottom >
          metrics.top + stickyHeight;

      let nextActive =
        targetRects[0]?.part || activePart;

      if (navigationLockPart) {
        const locked = targetRects.find(
          (target) =>
            target.part === navigationLockPart
        );

        const lockedOffset =
          metrics.top + stickyHeight + 12;

        if (
          locked &&
          Math.abs(
            locked.rect.top - lockedOffset
          ) > 4
        ) {
          nextActive = navigationLockPart;
        } else {
          releaseNavigationLock(false);
        }
      }

      if (!navigationLockPart) {
        targetRects.forEach((target) => {
          if (target.rect.top <= marker) {
            nextActive = target.part;
          }
        });

        if (
          window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - 4 &&
          targetRects.length
        ) {
          nextActive =
            targetRects[targetRects.length - 1].part;
        }
      }

      applyStickyState(shouldFix, metrics);
      setActive(nextActive);
    };

    const requestNavigationUpdate = () => {
      if (navigationFrame !== null) return;

      navigationFrame =
        requestAnimationFrame(updateNavigation);
    };

    const findPart = (part) =>
      productPartMap.get(part) ||
      document.querySelector(
        `[data-wi-product-part="${part}"]`
      );

    const scrollToElement = (target) => {
      if (!target) return;

      const metrics = getHeaderMetrics();

      const offset =
        metrics.top +
        (sticky?.offsetHeight || 0) +
        12;

      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY +
            target.getBoundingClientRect().top -
            offset
        ),
        behavior: reducedMotion
          ? 'auto'
          : 'smooth'
      });
    };

    const handlePartClick = (button) => {
      const part = button.dataset.wiTargetPart;
      const target = findPart(part);

      if (!target) return;

      setActive(part, true);
      lockNavigationTo(part);
      scrollToElement(target);
    };

    const setStickyCartState = (
      state,
      message
    ) => {
      stickySubmitButtons.forEach((button) => {
        const label = button.querySelector(
          '[data-wi-submit-label]'
        );

        if (!button.dataset.wiDefaultLabel) {
          button.dataset.wiDefaultLabel =
            label?.textContent?.trim() ||
            button.textContent.trim();
        }

        button.classList.toggle(
          'wi-state-loading',
          state === 'loading'
        );

        button.classList.toggle(
          'wi-state-success',
          state === 'success'
        );

        button.classList.toggle(
          'wi-state-error',
          state === 'error'
        );

        button.setAttribute(
          'aria-busy',
          state === 'loading'
            ? 'true'
            : 'false'
        );

        button.disabled = state === 'loading';

        if (label) {
          label.textContent =
            message ||
            button.dataset.wiDefaultLabel;
        }
      });

      if (primarySubmit) {
        if (!primarySubmit.dataset.wiDefaultLabel) {
          primarySubmit.dataset.wiDefaultLabel =
            primarySubmitLabel?.textContent?.trim() ||
            primarySubmit.textContent.trim();
        }

        primarySubmit.classList.toggle(
          'wi-state-loading',
          state === 'loading'
        );

        primarySubmit.classList.toggle(
          'wi-state-success',
          state === 'success'
        );

        primarySubmit.classList.toggle(
          'wi-state-error',
          state === 'error'
        );

        primarySubmit.setAttribute(
          'aria-busy',
          state === 'loading'
            ? 'true'
            : 'false'
        );

        primarySubmit.disabled = state === 'loading';

        if (primarySubmitLabel) {
          primarySubmitLabel.textContent =
            message ||
            primarySubmit.dataset.wiDefaultLabel;
        }
      }
    };

    const scheduleStickyCartReset = (
      delay = 1400
    ) => {
      if (stickyCartResetTimer !== null) {
        clearTimeout(stickyCartResetTimer);
      }

      stickyCartResetTimer = setTimeout(() => {
        setStickyCartState('idle');
        stickyCartResetTimer = null;
      }, delay);
    };

    const getCartRenderer = () =>
      document.querySelector('cart-drawer') ||
      document.querySelector(
        'cart-notification'
      );

    const getRequestedCartSections = (
      renderer
    ) => {
      const fromRenderer =
        typeof renderer?.getSectionsToRender ===
        'function'
          ? renderer
              .getSectionsToRender()
              .map((section) => section.id)
              .filter(Boolean)
          : [];

      return [
        ...new Set(
          fromRenderer.length
            ? fromRenderer
            : [
                'cart-drawer',
                'cart-icon-bubble'
              ]
        )
      ];
    };

    const cartCountSelector = [
      '.wi-header__cart-count',
      '[data-cart-count]',
      '[data-header-cart-count]',
      '[data-js-cart-count]',
      '.header__cart-count',
      '.site-header__cart-count',
      '.menu-meta__count',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span:first-child'
    ].join(',');

    const normaliseCartCount = (value) =>
      Math.max(
        0,
        parseInt(value, 10) || 0
      );

    const ensureCustomHeaderCartCount = (
      count
    ) => {
      const nodes = [];

      document
        .querySelectorAll(
          '.wi-header__icon-link--cart'
        )
        .forEach((link) => {
          let badge = link.querySelector(
            '.wi-header__cart-count'
          );

          if (!badge && count > 0) {
            badge = document.createElement('span');

            badge.className =
              'wi-header__cart-count';

            badge.setAttribute(
              'aria-live',
              'polite'
            );

            link.appendChild(badge);
          }

          if (badge) {
            nodes.push(badge);
          }
        });

      return nodes;
    };

    const updateCartCountDisplay = (
      value
    ) => {
      const count =
        normaliseCartCount(value);

      const nodes = Array.from(
        document.querySelectorAll(
          cartCountSelector
        )
      );

      ensureCustomHeaderCartCount(
        count
      ).forEach((node) => {
        if (!nodes.includes(node)) {
          nodes.push(node);
        }
      });

      nodes.forEach((node) => {
        node.textContent =
          count > 99 ? '99+' : String(count);

        node.setAttribute(
          'data-count',
          String(count)
        );

        node.hidden = count === 0;

        node.classList.toggle(
          'hidden',
          count === 0
        );

        node.classList.toggle(
          'hide',
          count === 0
        );
      });

      document
        .querySelectorAll('.cart-count-bubble')
        .forEach((bubble) => {
          bubble.hidden = count === 0;

          bubble.classList.toggle(
            'hidden',
            count === 0
          );

          bubble.classList.toggle(
            'hide',
            count === 0
          );
        });
    };

    const readDisplayedCartCount = () => {
      const node = document.querySelector(
        [
          '.wi-header__cart-count',
          '[data-cart-count]',
          '.header__cart-count',
          '.site-header__cart-count',
          '.cart-count-bubble span[aria-hidden="true"]'
        ].join(',')
      );

      if (!node) return null;

      const count = parseInt(
        node.textContent,
        10
      );

      return Number.isFinite(count)
        ? count
        : null;
    };

    const updateFallbackCartCount =
      async () => {
        try {
          const response = await fetch(
            cartUrl,
            {
              method: 'GET',
              credentials: 'same-origin',
              cache: 'no-store',
              headers: {
                Accept: 'application/json'
              }
            }
          );

          if (!response.ok) return null;

          const cart = await response.json();

          updateCartCountDisplay(
            cart.item_count
          );

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                cart,
                source: 'wi-sticky-cart'
              }
            })
          );

          document.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                cart,
                source: 'wi-sticky-cart'
              }
            })
          );

          return cart;
        } catch (error) {
          console.warn(
            '[WI Abode Product Gallery] Cart count refresh failed.',
            error
          );

          return null;
        }
      };

    const submitProductForm = async (
      event,
      trigger = null
    ) => {
      event?.preventDefault();
      event?.stopPropagation?.();

      const submitter =
        trigger ||
        event?.submitter ||
        document.activeElement ||
        primarySubmit;

      if (
        stickyCartBusy ||
        !productForm ||
        !primarySubmit
      ) {
        return;
      }

      if (primarySubmit.disabled) {
        scrollToElement(
          root.querySelector(
            '[data-wi-variant-picker]'
          ) ||
            productInfo ||
            productForm
        );

        return;
      }

      if (engravingInput && !engravingInput.disabled) {
        engravingInput.value = engravingInput.value.trim();
        engravingInput.setCustomValidity(
          engravingInput.value ? '' : 'Please enter the name to engrave.'
        );
      }

      if (typeof productForm.reportValidity === 'function' && !productForm.reportValidity()) {
        const invalidField = productForm.querySelector(':invalid');
        invalidField?.focus({ preventScroll: true });
        scrollToElement(invalidField || productInfo || productForm);
        return;
      }

      stickyCartBusy = true;

      setStickyCartState(
        'loading',
        'Adding…'
      );

      const formData =
        new FormData(productForm);

      const cartRenderer =
        getCartRenderer();

      const requestedSections =
        getRequestedCartSections(
          cartRenderer
        );

      if (
        typeof cartRenderer?.setActiveElement ===
        'function'
      ) {
        cartRenderer.setActiveElement(
          submitter
        );
      }

      const previousCartCount =
        readDisplayedCartCount();

      const requestedQuantity = Math.max(
        1,
        parseInt(
          formData.get('quantity'),
          10
        ) || 1
      );

      const optimisticCartCount =
        previousCartCount === null
          ? null
          : previousCartCount +
            requestedQuantity;

      if (
        optimisticCartCount !== null
      ) {
        updateCartCountDisplay(
          optimisticCartCount
        );
      }

      formData.set(
        'sections',
        requestedSections.join(',')
      );

      formData.set(
        'sections_url',
        window.location.pathname
      );

      try {
        const response = await fetch(
          cartAddUrl,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With':
                'XMLHttpRequest'
            },
            body: formData
          }
        );

        const result =
          await response.json();

        if (
          !response.ok ||
          result.status
        ) {
          throw new Error(
            result.description ||
              result.message ||
              'Unable to add this product to the cart.'
          );
        }

        if (
          typeof cartRenderer?.renderContents ===
          'function'
        ) {
          cartRenderer.renderContents(
            result
          );
        }

        if (
          cartRenderer?.tagName ===
            'CART-DRAWER' &&
          typeof cartRenderer.open ===
            'function'
        ) {
          window.requestAnimationFrame(() => {
            cartRenderer.open(
              submitter
            );
          });
        }

        updateFallbackCartCount();

        document.dispatchEvent(
          new CustomEvent(
            'wi:cart-item-added',
            {
              bubbles: true,
              detail: {
                item: result,
                source: 'wi-product-page'
              }
            }
          )
        );

        setStickyCartState(
          'success',
          'Added ✓'
        );

        scheduleStickyCartReset(1400);
      } catch (error) {
        if (
          previousCartCount !== null
        ) {
          updateCartCountDisplay(
            previousCartCount
          );
        }

        console.error(
          '[WI Abode Product Gallery] Sticky add to cart failed.',
          error
        );

        setStickyCartState(
          'error',
          'Try again'
        );

        scheduleStickyCartReset(1900);
      } finally {
        stickyCartBusy = false;
      }
    };

    navButtons.forEach((button) =>
      button.addEventListener(
        'click',
        () => handlePartClick(button)
      )
    );

    root
      .querySelectorAll(
        '[data-wi-target-part]'
      )
      .forEach((button) => {
        if (navButtons.includes(button)) {
          return;
        }

        button.addEventListener(
          'click',
          () => handlePartClick(button)
        );
      });

    productForm?.addEventListener(
      'submit',
      submitProductForm
    );

    const handleStickySubmitClick = (
      event
    ) => {
      const button =
        event.target.closest?.(
          '[data-wi-submit-form]'
        );

      if (
        !button ||
        !root.contains(button)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      submitProductForm(
        event,
        button
      );
    };

    root.addEventListener(
      'click',
      handleStickySubmitClick,
      true
    );

    const handleNavigationResize = () => {
      refreshHeaderCandidates();

      requestEngravingPreviewFit();

      requestIndicator(
        navButtons.find((button) =>
          button.classList.contains(
            'is-active'
          )
        )
      );

      requestNavigationUpdate();
    };

    const handleManualNavigation = () => {
      if (navigationLockPart) {
        releaseNavigationLock();
      }
    };

    const handleNavigationKey = (
      event
    ) => {
      if (
        [
          'PageUp',
          'PageDown',
          'Home',
          'End',
          'ArrowUp',
          'ArrowDown',
          ' '
        ].includes(event.key)
      ) {
        handleManualNavigation();
      }
    };

    const handleScrollEnd = () =>
      releaseNavigationLock();

    const handleSectionStructureChange =
      () => {
        refreshStructure();
        setActive(activePart, true);
        requestNavigationUpdate();
      };

    window.addEventListener(
      'scroll',
      requestNavigationUpdate,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      handleNavigationResize
    );

    window.addEventListener(
      'scrollend',
      handleScrollEnd
    );

    window.addEventListener(
      'wheel',
      handleManualNavigation,
      {
        passive: true
      }
    );

    window.addEventListener(
      'touchstart',
      handleManualNavigation,
      {
        passive: true
      }
    );

    window.addEventListener(
      'keydown',
      handleNavigationKey
    );

    document.addEventListener(
      'shopify:section:load',
      handleSectionStructureChange
    );

    document.addEventListener(
      'shopify:section:unload',
      handleSectionStructureChange
    );

    const startNavigation = () => {
      refreshStructure();
      setActive(activePart, true);
      updateNavigation();
    };

    if (
      document.readyState === 'loading'
    ) {
      document.addEventListener(
        'DOMContentLoaded',
        startNavigation,
        {
          once: true
        }
      );
    } else {
      startNavigation();
    }

    const syncReviews = (
      source = document.querySelector(
        '[data-wi-product-part="reviews"]'
      )
    ) => {
      if (!source) return;

      root
        .querySelector(
          '[data-wi-rating-value]'
        )
        ?.replaceChildren(
          source.dataset.wiRatingValue || ''
        );

      root
        .querySelector(
          '[data-wi-rating-count]'
        )
        ?.replaceChildren(
          source.dataset.wiRatingCount || ''
        );
    };

    const handleReviewsSummary = (
      event
    ) => {
      syncReviews(event.detail?.target);
    };

    document.addEventListener(
      'wi:reviews-summary',
      handleReviewsSummary
    );

    syncReviews();

    const mediaItems = Array.from(
      root.querySelectorAll(
        '[data-wi-media]'
      )
    );

    const stage = root.querySelector(
      '[data-wi-media-stage]'
    );

    const thumbnails = Array.from(
      root.querySelectorAll(
        '[data-wi-thumbnail]'
      )
    );

    const progress = Array.from(
      root.querySelectorAll(
        '[data-wi-progress]'
      )
    );

    const track = root.querySelector(
      '[data-wi-thumb-track]'
    );

    let activeIndex = Math.max(
      0,
      thumbnails.findIndex((item) =>
        item.classList.contains(
          'is-active'
        )
      )
    );

    const updateThumbArrows = () => {
      if (!track) return;

      const max = Math.max(
        0,
        track.scrollWidth -
          track.clientWidth
      );

      const prev = root.querySelector(
        '[data-wi-thumb-prev]'
      );

      const next = root.querySelector(
        '[data-wi-thumb-next]'
      );

      if (prev) {
        prev.disabled =
          track.scrollLeft <= 1;
      }

      if (next) {
        next.disabled =
          track.scrollLeft >= max - 1;
      }
    };

    const keepThumbnailVisible = (
      thumb
    ) => {
      if (!track || !thumb) return;

      const left = thumb.offsetLeft;
      const right =
        left + thumb.offsetWidth;

      const visibleLeft =
        track.scrollLeft;

      const visibleRight =
        visibleLeft + track.clientWidth;

      if (
        left >= visibleLeft + 2 &&
        right <= visibleRight - 2
      ) {
        return;
      }

      const next =
        left -
        (track.clientWidth -
          thumb.offsetWidth) /
          2;

      track.scrollTo({
        left: Math.max(0, next),
        behavior: reducedMotion
          ? 'auto'
          : 'smooth'
      });
    };

    const showMedia = (requested) => {
      if (!thumbnails.length) return;

      activeIndex =
        (requested +
          thumbnails.length) %
        thumbnails.length;

      const thumb =
        thumbnails[activeIndex];

      const id = thumb.dataset.mediaId;

      mediaItems.forEach((item) => {
        const active =
          item.dataset.mediaId === id;

        item.hidden = !active;

        item.classList.toggle(
          'is-active',
          active
        );

        if (!active) {
          item
            .querySelector('video')
            ?.pause();
        }
      });

      thumbnails.forEach(
        (item, index) => {
          const active =
            index === activeIndex;

          item.classList.toggle(
            'is-active',
            active
          );

          item.setAttribute(
            'aria-current',
            active ? 'true' : 'false'
          );
        }
      );

      progress.forEach(
        (item, index) =>
          item.classList.toggle(
            'is-active',
            index === activeIndex
          )
      );

      keepThumbnailVisible(thumb);
    };

    thumbnails.forEach(
      (item, index) =>
        item.addEventListener(
          'click',
          () => showMedia(index)
        )
    );

    root
      .querySelector(
        '[data-wi-gallery-prev]'
      )
      ?.addEventListener(
        'click',
        () =>
          showMedia(activeIndex - 1)
      );

    root
      .querySelector(
        '[data-wi-gallery-next]'
      )
      ?.addEventListener(
        'click',
        () =>
          showMedia(activeIndex + 1)
      );

    root
      .querySelector(
        '[data-wi-thumb-prev]'
      )
      ?.addEventListener(
        'click',
        () =>
          track?.scrollBy({
            left: -Math.max(
              100,
              track.clientWidth * 0.72
            ),
            behavior: reducedMotion
              ? 'auto'
              : 'smooth'
          })
      );

    root
      .querySelector(
        '[data-wi-thumb-next]'
      )
      ?.addEventListener(
        'click',
        () =>
          track?.scrollBy({
            left: Math.max(
              100,
              track.clientWidth * 0.72
            ),
            behavior: reducedMotion
              ? 'auto'
              : 'smooth'
          })
      );

    track?.addEventListener(
      'scroll',
      updateThumbArrows,
      {
        passive: true
      }
    );

    updateThumbArrows();

    let startX = null;

    stage?.addEventListener(
      'touchstart',
      (event) => {
        startX = event.target.closest(
          'video'
        )
          ? null
          : event.changedTouches[0]
              .clientX;
      },
      {
        passive: true
      }
    );

    stage?.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;

        const distance =
          event.changedTouches[0]
            .clientX - startX;

        if (
          Math.abs(distance) > 45
        ) {
          showMedia(
            activeIndex +
              (distance < 0 ? 1 : -1)
          );
        }

        startX = null;
      },
      {
        passive: true
      }
    );

    const quantity = root.querySelector(
      '[data-wi-quantity]'
    );

    const buttonPrice = root.querySelector(
      '[data-wi-button-price]'
    );

    const activeVariantInput = root.querySelector(
      '[data-wi-active-variant]'
    );

    const modeButtons = Array.from(
      root.querySelectorAll('[data-wi-product-mode-value]')
    );

    const modePanels = Array.from(
      root.querySelectorAll('[data-wi-mode-panel]')
    );

    const engravingInput = root.querySelector(
      '[data-wi-engraving-input]'
    );

    const engravingCount = root.querySelector(
      '[data-wi-engraving-count]'
    );

    const engravingPreview = root.querySelector(
      '[data-wi-engraving-preview]'
    );

    const engravingPreviewZone = root.querySelector(
      '[data-wi-engraving-preview-zone]'
    );

    const engravingPreviewText = root.querySelector(
      '[data-wi-engraving-preview-text]'
    );

    const productConfigs = config.products || {};
    let activeMode = 'standard';
    let unitPrice = Number(config.unitPrice || 0);

    const setQuantity = (value) => {
      if (!quantity) return;

      const next = Math.max(
        1,
        parseInt(value, 10) || 1
      );

      quantity.value = next;

      if (buttonPrice && unitPrice > 0) {
        buttonPrice.textContent =
          ` · ${formatMoney(unitPrice * next)}`;
      }
    };

    root
      .querySelector('[data-wi-minus]')
      ?.addEventListener('click', () =>
        setQuantity(Number(quantity?.value || 1) - 1)
      );

    root
      .querySelector('[data-wi-plus]')
      ?.addEventListener('click', () =>
        setQuantity(Number(quantity?.value || 1) + 1)
      );

    quantity?.addEventListener('change', () =>
      setQuantity(quantity.value)
    );

    let engravingPreviewFrame = null;

    const fitEngravingPreview = () => {
      engravingPreviewFrame = null;

      if (!engravingPreviewZone || !engravingPreviewText) return;

      engravingPreviewText.style.setProperty(
        '--wi-engraving-preview-scale',
        '1'
      );

      const availableWidth = engravingPreviewZone.clientWidth;
      const textWidth = engravingPreviewText.scrollWidth;

      if (availableWidth <= 0 || textWidth <= 0) return;

      const scale = Math.min(
        1,
        Math.max(0.58, availableWidth / textWidth)
      );

      engravingPreviewText.style.setProperty(
        '--wi-engraving-preview-scale',
        scale.toFixed(3)
      );
    };

    const requestEngravingPreviewFit = () => {
      if (engravingPreviewFrame !== null) {
        cancelAnimationFrame(engravingPreviewFrame);
      }

      engravingPreviewFrame = requestAnimationFrame(
        fitEngravingPreview
      );
    };

    const updateEngravingPreview = () => {
      if (!engravingInput) return;

      const maxLength = Math.max(
        1,
        Number(engravingInput.maxLength || 10)
      );

      if (engravingInput.value.length > maxLength) {
        engravingInput.value = engravingInput.value.slice(
          0,
          maxLength
        );
      }

      engravingInput.setCustomValidity('');

      if (engravingCount) {
        engravingCount.textContent = String(
          engravingInput.value.length
        );
      }

      if (engravingPreviewText) {
        const value = engravingInput.value.trim();
        const placeholder =
          engravingPreviewText.dataset.placeholder || '';

        engravingPreviewText.textContent =
          value || placeholder;

        engravingPreviewText.classList.toggle(
          'is-placeholder',
          !value
        );

        requestEngravingPreviewFit();
      }
    };

    const handleEngravingInput = () => {
      updateEngravingPreview();
    };

    engravingInput?.addEventListener(
      'input',
      handleEngravingInput
    );

    updateEngravingPreview();

    const packBadgeConfig = {
      enabled: Boolean(config.packBadges?.enabled),
      superSaver: String(config.packBadges?.superSaver || ''),
      mostBought: String(config.packBadges?.mostBought || '')
    };

    const optionKey = '\u001f';
    const panelStates = new Map();

    const showVariantMedia = (option) => {
      const mediaId = option?.dataset?.mediaId;
      if (!mediaId) return;

      const mediaThumb = thumbnails.find(
        (item) => item.dataset.mediaId === mediaId
      );

      if (mediaThumb) {
        showMedia(thumbnails.indexOf(mediaThumb));
      }
    };

    const applyActiveVariant = (state) => {
      if (!state || state.mode !== activeMode) return;

      const option = state.variantSelect?.options[
        state.variantSelect.selectedIndex
      ];

      if (!option) return;

      const price = Number(option.dataset.priceRaw || 0);
      const compare = Number(option.dataset.compareRaw || 0);
      const available = option.dataset.available !== 'false';

      unitPrice = price;

      if (activeVariantInput) {
        activeVariantInput.value = state.variantSelect.value;
      }

      root
        .querySelectorAll('[data-wi-price],[data-wi-mobile-price]')
        .forEach((element) => {
          element.textContent =
            option.dataset.price || formatMoney(price);
        });

      const compareEl = root.querySelector('[data-wi-compare]');
      const discount = root.querySelector('[data-wi-discount]');

      if (compareEl && discount) {
        const sale = compare > price;
        compareEl.hidden = !sale;
        discount.hidden = !sale;

        if (sale) {
          compareEl.textContent =
            option.dataset.compare || formatMoney(compare);

          discount.textContent =
            `SAVE ${Math.round(((compare - price) / compare) * 100)}%`;
        }
      }

      if (primarySubmit) {
        primarySubmit.disabled = !available;
        if (primarySubmitLabel) {
          primarySubmitLabel.textContent = available
            ? (config.addToCartText || 'Add To Cart')
            : (config.soldOutText || 'SOLD OUT');
        }
      }

      if (activeMode === 'standard') {
        showVariantMedia(option);
      }

      setQuantity(quantity?.value || 1);

      document.dispatchEvent(
        new CustomEvent('wi:product-variant-change', {
          detail: {
            price,
            formattedPrice: option.dataset.price,
            variantId: state.variantSelect.value,
            productMode: activeMode
          }
        })
      );
    };

    const createPanelState = (panel) => {
      const mode = panel.dataset.wiModePanel || 'standard';
      const variantPicker = panel.querySelector('[data-wi-variant-picker]');
      const variantSelect = panel.querySelector('[data-wi-variant]');
      const optionChoices = Array.from(
        panel.querySelectorAll('[data-wi-option-value]')
      );
      const optionGroups = Array.from(
        panel.querySelectorAll('[data-wi-option-group]')
      );
      const derivedPicker =
        variantPicker?.dataset.wiDerivedPicker === 'true';
      const variantData = Array.isArray(
        productConfigs?.[mode]?.variants
      )
        ? productConfigs[mode].variants
        : [];

      const splitVariantTitle = (title) =>
        String(title || '')
          .split(' / ')
          .map((value) => value.trim());

      const getVariantValues = (variant) =>
        derivedPicker
          ? splitVariantTitle(variant?.title)
          : Array.from(variant?.options || []).map((value) =>
              String(value)
            );

      const variantRows = variantData.map((variant) => ({
        variant,
        values: getVariantValues(variant)
      }));

      const availableVariantRows = variantRows.filter(
        (row) => row.variant.available
      );

      const exactAvailableVariantMap = new Map();
      const firstAvailableByPrefix = Array.from(
        { length: Math.max(1, optionGroups.length) },
        () => new Map()
      );
      const availabilityByPosition = Array.from(
        { length: Math.max(1, optionGroups.length) },
        () => new Map()
      );

      availableVariantRows.forEach((row) => {
        exactAvailableVariantMap.set(
          row.values.join(optionKey),
          row
        );

        row.values.forEach((value, position) => {
          const exactPrefix = row.values
            .slice(0, position + 1)
            .join(optionKey);

          if (!firstAvailableByPrefix[position].has(exactPrefix)) {
            firstAvailableByPrefix[position].set(exactPrefix, row);
          }

          const precedingPrefix = row.values
            .slice(0, position)
            .join(optionKey);

          let availableValues =
            availabilityByPosition[position].get(precedingPrefix);

          if (!availableValues) {
            availableValues = new Set();
            availabilityByPosition[position].set(
              precedingPrefix,
              availableValues
            );
          }

          availableValues.add(value);
        });
      });

      const badgeElements = new Map();
      let selectedOptions = [];

      const getCurrentVariantRow = () =>
        variantRows.find(
          (row) =>
            String(row.variant.id) === String(variantSelect?.value)
        );

      const updateOptionAvailability = () => {
        optionChoices.forEach((choice) => {
          const position = Math.max(
            0,
            Number(choice.dataset.optionPosition || 1) - 1
          );

          const value = choice.dataset.optionValue || '';
          const precedingPrefix = selectedOptions
            .slice(0, position)
            .join(optionKey);

          const available =
            availabilityByPosition[position]
              ?.get(precedingPrefix)
              ?.has(value) === true;

          choice.disabled = !available;
          choice.setAttribute(
            'aria-disabled',
            available ? 'false' : 'true'
          );
        });
      };

      const removePackBadges = () => {
        optionChoices.forEach((choice) =>
          choice.classList.remove(
            'has-pack-badge',
            'has-pack-badge--super',
            'has-pack-badge--popular'
          )
        );

        optionGroups.forEach((group) =>
          group.classList.remove('has-dynamic-badges')
        );

        badgeElements.forEach((badge) => {
          badge.hidden = true;
        });
      };

      const addPackBadge = (choice, label, type) => {
        if (!choice || !label) return;

        let badge = badgeElements.get(type);

        if (!badge) {
          badge = document.createElement('span');
          badge.className =
            `wi-pg__variant-badge wi-pg__variant-badge--${type}`;
          badge.dataset.wiPackBadge = type;
          badge.setAttribute('aria-hidden', 'true');
          badgeElements.set(type, badge);
        }

        badge.textContent = label;

        if (badge.parentElement !== choice) {
          choice.appendChild(badge);
        }

        badge.hidden = false;
        choice.classList.add(
          'has-pack-badge',
          `has-pack-badge--${type}`
        );
      };

      const getPricedChoicesForGroup = (group) => {
        if (!group) return [];

        const position = Math.max(
          0,
          Number(group.dataset.optionPosition || 1) - 1
        );

        const choices = Array.from(
          group.querySelectorAll('[data-wi-option-value]')
        ).filter((choice) => !choice.disabled);

        const minimumPriceByValue = new Map();

        availableVariantRows.forEach((row) => {
          const matchesOtherSelections = row.values.every(
            (rowValue, rowIndex) =>
              rowIndex === position ||
              selectedOptions[rowIndex] == null ||
              rowValue === selectedOptions[rowIndex]
          );

          if (!matchesOtherSelections) return;

          const value = row.values[position];
          const price = Number(row.variant.price || 0);

          if (!Number.isFinite(price)) return;

          const current = minimumPriceByValue.get(value);
          if (current == null || price < current) {
            minimumPriceByValue.set(value, price);
          }
        });

        return choices
          .map((choice, index) => {
            const price = minimumPriceByValue.get(
              choice.dataset.optionValue || ''
            );

            return price == null
              ? null
              : { choice, index, price };
          })
          .filter(Boolean)
          .sort((a, b) => a.price - b.price || a.index - b.index);
      };

      const groupLooksCommercial = (group) => {
        if (
          !group ||
          group.classList.contains('is-color') ||
          group.classList.contains('is-size')
        ) {
          return false;
        }

        if (
          group.classList.contains('is-pack') ||
          group.classList.contains('is-offer')
        ) {
          return true;
        }

        const labels = Array.from(
          group.querySelectorAll('[data-wi-option-value]')
        )
          .map(
            (choice) =>
              `${choice.dataset.optionValue || ''} ${choice.dataset.displayValue || ''}`
          )
          .join(' ');

        return /\b(pack|packs|pair|pairs|combo|offer|bundle|bundles|set|sets|piece|pieces|pcs|quantity|qty)\b/i.test(
          labels
        );
      };

      const findBadgeGroup = () => {
        const explicit =
          optionGroups.find((group) => group.classList.contains('is-pack')) ||
          optionGroups.find((group) => group.classList.contains('is-offer'));

        if (explicit) return explicit;

        const valueDriven = optionGroups.find(groupLooksCommercial);
        if (valueDriven) return valueDriven;

        if (optionGroups.length === 1) {
          const onlyGroup = optionGroups[0];
          if (
            !onlyGroup.classList.contains('is-color') &&
            !onlyGroup.classList.contains('is-size')
          ) {
            const priced = getPricedChoicesForGroup(onlyGroup);
            const uniquePrices = new Set(priced.map((item) => item.price));
            if (
              priced.length >= 2 &&
              (groupLooksCommercial(onlyGroup) || uniquePrices.size > 1)
            ) {
              return onlyGroup;
            }
          }
        }

        return null;
      };

      const updatePackBadges = () => {
        removePackBadges();
        if (!packBadgeConfig.enabled) return;

        const badgeGroup = findBadgeGroup();
        if (!badgeGroup) return;

        const pricedChoices = getPricedChoicesForGroup(badgeGroup);
        if (pricedChoices.length < 2) return;

        badgeGroup.classList.add('has-dynamic-badges');

        const superSaver = pricedChoices[pricedChoices.length - 1];
        addPackBadge(
          superSaver.choice,
          packBadgeConfig.superSaver,
          'super'
        );

        const popularIndex = pricedChoices.length >= 3 ? 1 : 0;
        let mostBought = pricedChoices[popularIndex];
        if (mostBought === superSaver) mostBought = pricedChoices[0];

        addPackBadge(
          mostBought.choice,
          packBadgeConfig.mostBought,
          'popular'
        );
      };

      const sync = () => {
        const current = getCurrentVariantRow();
        if (!current) return;

        selectedOptions = [...current.values];

        optionChoices.forEach((choice) => {
          const position = Math.max(
            0,
            Number(choice.dataset.optionPosition || 1) - 1
          );

          const active =
            choice.dataset.optionValue === selectedOptions[position];

          choice.classList.toggle('is-active', active);
          choice.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        optionGroups.forEach((group) => {
          const position = Math.max(
            0,
            Number(group.dataset.optionPosition || 1) - 1
          );

          const activeChoice = optionChoices.find(
            (choice) =>
              Number(choice.dataset.optionPosition || 1) - 1 === position &&
              choice.dataset.optionValue === selectedOptions[position]
          );

          const label = group.querySelector('[data-wi-selected-option]');
          if (label) {
            label.textContent =
              activeChoice?.dataset.displayValue ||
              selectedOptions[position] ||
              '';
          }
        });

        updateOptionAvailability();
        updatePackBadges();
      };

      const state = {
        mode,
        panel,
        variantSelect,
        optionChoices,
        optionGroups,
        sync
      };

      variantSelect?.addEventListener('change', () => {
        sync();
        applyActiveVariant(state);
      });

      optionChoices.forEach((choice) =>
        choice.addEventListener('click', () => {
          if (!variantSelect || choice.disabled) return;

          const position = Math.max(
            0,
            Number(choice.dataset.optionPosition || 1) - 1
          );

          const requested = [...selectedOptions];
          requested[position] = choice.dataset.optionValue || '';

          let target = exactAvailableVariantMap.get(
            requested.join(optionKey)
          );

          if (!target) {
            const requestedPrefix = requested
              .slice(0, position + 1)
              .join(optionKey);

            target = firstAvailableByPrefix[position]?.get(
              requestedPrefix
            );
          }

          if (!target) return;

          variantSelect.value = String(target.variant.id);
          variantSelect.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        })
      );

      sync();
      return state;
    };

    modePanels.forEach((panel) => {
      const state = createPanelState(panel);
      panelStates.set(state.mode, state);
    });

    const setProductMode = (mode, focus = false) => {
      if (!panelStates.has(mode)) return;

      activeMode = mode;

      modeButtons.forEach((button) => {
        const active = button.dataset.wiProductModeValue === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      modePanels.forEach((panel) => {
        panel.hidden = panel.dataset.wiModePanel !== mode;
      });

      if (engravingInput) {
        const isCustom = mode === 'customized';
        engravingInput.disabled = !isCustom;
        engravingInput.required = isCustom;
        if (isCustom && focus) {
          window.requestAnimationFrame(() => engravingInput.focus());
        }
      }

      const state = panelStates.get(mode);
      state?.sync();
      applyActiveVariant(state);

      if (mode === 'customized' && engravingPreview) {
        window.requestAnimationFrame(() => {
          updateEngravingPreview();
        });
      }
    };

    modeButtons.forEach((button) =>
      button.addEventListener('click', () =>
        setProductMode(
          button.dataset.wiProductModeValue || 'standard',
          false
        )
      )
    );

    setProductMode('standard');

    root.wiCleanup = () => {
      window.removeEventListener(
        'scroll',
        requestNavigationUpdate
      );

      window.removeEventListener(
        'resize',
        handleNavigationResize
      );

      window.removeEventListener(
        'scrollend',
        handleScrollEnd
      );

      window.removeEventListener(
        'wheel',
        handleManualNavigation
      );

      window.removeEventListener(
        'touchstart',
        handleManualNavigation
      );

      window.removeEventListener(
        'keydown',
        handleNavigationKey
      );

      document.removeEventListener(
        'shopify:section:load',
        handleSectionStructureChange
      );

      document.removeEventListener(
        'shopify:section:unload',
        handleSectionStructureChange
      );

      document.removeEventListener(
        'wi:reviews-summary',
        handleReviewsSummary
      );

      root.removeEventListener(
        'click',
        handleStickySubmitClick,
        true
      );

      engravingInput?.removeEventListener(
        'input',
        handleEngravingInput
      );

      if (engravingPreviewFrame !== null) {
        cancelAnimationFrame(
          engravingPreviewFrame
        );
      }

      if (
        navigationFrame !== null
      ) {
        cancelAnimationFrame(
          navigationFrame
        );
      }

      if (indicatorFrame !== null) {
        cancelAnimationFrame(
          indicatorFrame
        );
      }

      if (
        navigationLockTimer !== null
      ) {
        clearTimeout(
          navigationLockTimer
        );
      }

      if (
        stickyCartResetTimer !== null
      ) {
        clearTimeout(
          stickyCartResetTimer
        );
      }

      sticky?.classList.remove(
        'is-fixed'
      );

      sticky?.style.removeProperty(
        'top'
      );

      sticky?.style.removeProperty(
        'z-index'
      );
    };
  };

  window.WIAbodeProductGalleryInitialize =
    initialize;

  document
    .querySelectorAll(
      '[data-wi-product-gallery]'
    )
    .forEach(initialize);

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initialize(
        event.target.querySelector?.(
          '[data-wi-product-gallery]'
        )
      )
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) =>
      event.target
        .querySelector?.(
          '[data-wi-product-gallery]'
        )
        ?.wiCleanup?.()
  );
})();